import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";


export default function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [dhanId, setDhanId] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const nav = useNavigate();
  const { setUser } = useAuth();

  const checkCoupon = async () => {
    if (!coupon.trim()) return;
    setCheckingCoupon(true);
    setCouponInfo(null);
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon.trim() });
      setCouponInfo(data);
    } catch (e) {
      setCouponInfo({ valid: false, error: formatError(e.response?.data?.detail) || "Invalid coupon" });
    } finally { setCheckingCoupon(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/signup", {
        name, email, password: pw, dhan_client_id: dhanId.trim(),
        coupon: couponInfo?.valid ? couponInfo.code : undefined,
      });
      setUser(data);
      nav("/dashboard");
    } catch (e) {
      setErr(formatError(e.response?.data?.detail) || "Signup failed");
    } finally { setBusy(false); }
  };


  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-12 border-r border-zinc-900 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A] to-[#007AFF]/10">
        <Link to="/" className="flex items-center gap-2" data-testid="signup-logo">
          <img src="/brand/ptiq-icon.png" alt="ProTradeIQ" className="h-6 w-6 object-contain" />
          <span className="font-heading text-xl font-black uppercase">PROTRADE<span className="text-[#007AFF]">IQ</span></span>
        </Link>
        <div>
          <div className="text-xs font-mono tracking-[0.3em] text-[#007AFF] mb-4 uppercase">◢ Get started</div>
          <h2 className="font-heading text-5xl font-black uppercase tracking-tighter leading-none">
            Three minutes.<br />Then the trade.
          </h2>
          <div className="mt-6 border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-4" data-testid="dhan-only-notice">
            <div className="text-[10px] uppercase tracking-widest text-[#F59E0B] font-mono font-bold mb-2">⚠ DHAN account holders only</div>
            <ul className="space-y-1.5 text-xs text-zinc-300 leading-relaxed">
              <li>▸ ProTradeIQ currently operates <b>exclusively for DHAN users</b> — all 1-click execution and algo trades run on YOUR own DHAN account</li>
              <li>▸ You'll generate your own DHAN access token every morning — <b>your trades, your account, your control</b></li>
              <li>▸ No DHAN account yet? <a href="https://join.dhan.co/?invite=EVMCD04986" target="_blank" rel="noreferrer" className="text-[#22C55E] hover:underline font-bold">Open one free in ~10 min →</a></li>
            </ul>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="border border-zinc-800 bg-black/40 p-4 relative" data-testid="plan-monthly-card">
              <span className="absolute -top-2 left-3 bg-[#007AFF] text-white text-[8px] uppercase tracking-widest font-mono font-bold px-2 py-0.5">Introductory offer</span>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mt-1">Monthly</div>
              <div className="mt-1"><span className="font-heading text-2xl font-black">₹2,999</span><span className="text-xs text-zinc-500">/mo</span></div>
              <div className="text-[10px] text-zinc-500 line-through">₹4,999</div>
            </div>
            <div className="border border-[#22C55E]/50 bg-[#22C55E]/5 p-4 relative" data-testid="plan-annual-card">
              <span className="absolute -top-2 left-3 bg-[#22C55E] text-black text-[8px] uppercase tracking-widest font-mono font-bold px-2 py-0.5">Best value · Save ₹6,000</span>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mt-1">Annual</div>
              <div className="mt-1"><span className="font-heading text-2xl font-black text-[#22C55E]">₹29,999</span><span className="text-xs text-zinc-500">/yr</span></div>
              <div className="text-[10px] text-[#22C55E]">≈ ₹2,500/mo · 2 months free</div>
            </div>
          </div>
        </div>
        <div className="text-xs uppercase tracking-widest text-zinc-600">Trade only the high-conviction calls.</div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="signup-form">
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Create account</h1>
          <p className="mt-2 text-sm text-zinc-400">DHAN account holders only — you'll connect your own DHAN after signing up.</p>

          <label className="mt-8 block text-xs uppercase tracking-widest text-zinc-400">Full name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 w-full bg-transparent border border-zinc-800 focus:border-[#007AFF] outline-none px-3 py-3 text-sm" data-testid="signup-name" />

          <label className="text-xs uppercase tracking-widest text-zinc-400 mt-5 block">Email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} required type="email" className="mt-1 w-full bg-transparent border border-zinc-800 focus:border-[#007AFF] outline-none px-3 py-3 font-mono text-sm" data-testid="signup-email" />

          <label className="text-xs uppercase tracking-widest text-zinc-400 mt-5 block">Password</label>
          <input value={pw} onChange={(e) => setPw(e.target.value)} required minLength={6} type="password" className="mt-1 w-full bg-transparent border border-zinc-800 focus:border-[#007AFF] outline-none px-3 py-3 font-mono text-sm" data-testid="signup-password" />
          <div className="text-xs text-zinc-600 mt-1">At least 6 characters.</div>

          <label className="text-xs uppercase tracking-widest text-zinc-400 mt-5 block">DHAN Client ID <span className="text-[#F59E0B]">*</span></label>
          <input value={dhanId} onChange={(e) => setDhanId(e.target.value.replace(/[^0-9]/g, ""))} required minLength={6}
                 placeholder="e.g. 1100123456" inputMode="numeric"
                 className="mt-1 w-full bg-transparent border border-zinc-800 focus:border-[#F59E0B] outline-none px-3 py-3 font-mono text-sm" data-testid="signup-dhan-id" />
          <div className="text-xs text-zinc-600 mt-1">Mandatory — find it in the DHAN app under My Profile. No DHAN account? <a href="https://join.dhan.co/?invite=EVMCD04986" target="_blank" rel="noreferrer" className="text-[#22C55E] hover:underline">Open one first →</a></div>

          <label className="text-xs uppercase tracking-widest text-zinc-400 mt-5 block">Discount coupon <span className="text-zinc-600 normal-case">(optional)</span></label>
          <div className="mt-1 flex gap-2">
            <input value={coupon} onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setCouponInfo(null); }} placeholder="PTIQ-XXXXXX"
                   className="flex-1 bg-transparent border border-zinc-800 focus:border-[#22C55E] outline-none px-3 py-3 font-mono text-sm uppercase" data-testid="signup-coupon" />
            <button type="button" onClick={checkCoupon} disabled={checkingCoupon || !coupon.trim()}
                    className="border border-zinc-700 hover:border-[#22C55E] text-zinc-300 hover:text-[#22C55E] px-4 text-xs uppercase tracking-widest font-mono disabled:opacity-40"
                    data-testid="signup-coupon-apply">
              {checkingCoupon ? "…" : "Apply"}
            </button>
          </div>
          {couponInfo?.valid && (
            <div className="mt-2 text-xs text-[#22C55E] border border-[#22C55E]/30 bg-[#22C55E]/5 px-3 py-2 font-mono" data-testid="signup-coupon-ok">
              ✓ {couponInfo.code} applied — {couponInfo.percent_off}% OFF your subscription
              <span className="block text-zinc-400 mt-0.5">Monthly ₹{Math.round(2999 * (1 - couponInfo.percent_off / 100)).toLocaleString("en-IN")} · Annual ₹{Math.round(29999 * (1 - couponInfo.percent_off / 100)).toLocaleString("en-IN")}</span>
            </div>
          )}
          {couponInfo && !couponInfo.valid && (
            <div className="mt-2 text-xs text-[#EF4444] font-mono" data-testid="signup-coupon-bad">{couponInfo.error}</div>
          )}

          {err && <div className="mt-4 text-sm text-[#EF4444] border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2" data-testid="signup-error">{err}</div>}

          <button disabled={busy} type="submit" className="mt-6 w-full bg-[#007AFF] text-white font-bold uppercase tracking-wider py-3 hover:bg-[#3395FF] transition-colors disabled:opacity-50" data-testid="signup-submit">
            {busy ? "Creating…" : "Create account"}
          </button>

          <div className="mt-6 text-sm text-zinc-400 text-center">
            Already have an account? <Link to="/login" className="text-[#007AFF] hover:underline" data-testid="signup-go-login">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
